import { useState } from "react";
import { Plus, Pencil, UserX } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useFetch } from "../../lib/useData";
import { PageTitle, TableWrap, Btn, Modal, Input, Select, Badge } from "../../components/admin/ui";

const ROLES = [
  { value: "super_admin", label: "Super Admin" },
  { value: "content_admin", label: "Content Admin" },
  { value: "booking_admin", label: "Booking Admin" },
];
const PAGE_SIZES = [10, 50, 100, 500, 1000];
export default function Users() {
  const { data: users, loading, reload } = useFetch("/admin/users");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [pageSize, setPageSize] = useState(10);
const [page, setPage] = useState(1);

const allRows = users || [];
const total = allRows.length;
const totalPages = Math.max(1, Math.ceil(total / pageSize));
const currentPage = Math.min(page, totalPages);
const start = (currentPage - 1) * pageSize;
const pagedRows = allRows.slice(start, start + pageSize);

  const openNew = () => { setForm({ name: "", email: "", password: "", role: "content_admin", active: true }); setEditing("new"); };
  const openEdit = (u) => { setForm({ ...u, password: "" }); setEditing(u.id); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/users", form);
      else await api.put(`/admin/users/${editing}`, form);
      toast.success("Saved"); setEditing(null); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const deactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.email}? Their access will be revoked immediately.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Deactivated"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageTitle title="Admin Users" desc="Manage staff accounts and roles (RBAC)">
        <Btn onClick={openNew} testid="user-add"><Plus className="w-4 h-4" /> Add User</Btn>
      </PageTitle>
      <TableWrap>
        <thead className="bg-slate-800 text-slate-400"><tr>
          <th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Active</th><th className="text-right px-4 py-3">Actions</th>
        </tr></thead>
        <tbody className="divide-y divide-slate-700" data-testid="users-table">
          {loading ? <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr> :
pagedRows.map((u) => (
                <tr key={u.id} className="hover:bg-slate-800/50">
                <td className="px-4 py-3 font-semibold">{u.name}</td>
                <td className="px-4 py-3 text-slate-300">{u.email}</td>
                <td className="px-4 py-3 capitalize">{u.role?.replace(/_/g, " ")}</td>
                <td className="px-4 py-3"><Badge status={u.active ? "published" : "cancelled"} /></td>
                <td className="px-4 py-3"><div className="flex justify-end gap-1">
                  <button onClick={() => openEdit(u)} className="p-2 rounded-lg hover:bg-slate-700 text-blue-400" data-testid={`user-edit-${u.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => deactivate(u)} className="p-2 rounded-lg hover:bg-slate-700 text-red-400" data-testid={`user-deactivate-${u.id}`}><UserX className="w-4 h-4" /></button>
                </div></td>
              </tr>
            ))}
        </tbody>
      </TableWrap>
      {!loading && total > 0 && (
  <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm text-slate-300">
    <div className="flex items-center gap-2">
      <span>Show</span>
      <select
        value={pageSize}
        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
        className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-slate-200"
        data-testid="users-page-size"
      >
        {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <span>per page</span>
    </div>

    <span className="text-slate-400">
      {start + 1}–{Math.min(start + pageSize, total)} of {total}
    </span>

    <div className="flex items-center gap-2">
      <button
        onClick={() => setPage(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >Prev</button>
      <span>{currentPage} / {totalPages}</span>
      <button
        onClick={() => setPage(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >Next</button>
    </div>
  </div>
)}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "Add Admin User" : "Edit Admin User"}>
        <div className="space-y-4">
          <Input label="Name *" value={form.name} onChange={(v) => set("name", v)} testid="user-f-name" />
          <Input label="Email *" value={form.email} onChange={(v) => set("email", v)} testid="user-f-email" />
          <Input label={editing === "new" ? "Password *" : "New Password (leave blank to keep)"} type="password" value={form.password} onChange={(v) => set("password", v)} testid="user-f-password" />
          <Select label="Role" value={form.role} onChange={(v) => set("role", v)} options={ROLES} testid="user-f-role" />
          <Select label="Active" value={String(form.active)} onChange={(v) => set("active", v === "true")} options={[{ value: "true", label: "Active" }, { value: "false", label: "Inactive" }]} />
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Btn variant="outline" onClick={() => setEditing(null)}>Cancel</Btn>
          <Btn onClick={save} disabled={saving} testid="user-save">{saving ? "Saving…" : "Save"}</Btn>
        </div>
      </Modal>
    </div>
  );
}
